import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  subDays,
  getUnixTime,
  startOfDay,
  endOfDay,
  fromUnixTime,
} from 'date-fns';

export function useIspReport({
  getter,
  action,
  filters: filterDefs = {},
  initActions = [],
  paramsTransform = null, // GK-HUB: hook p/ derivar params extras (ex: date_field por status) (#27)
}) {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  // Initialize from URL query params (fallback: last 30 days)
  const urlFrom = route.query.from ? Number(route.query.from) : null;
  const urlTo = route.query.to ? Number(route.query.to) : null;
  const urlRange = route.query.range || 'last30days';

  const customDateRange = ref(
    urlFrom && urlTo
      ? [fromUnixTime(urlFrom), fromUnixTime(urlTo)]
      : [subDays(new Date(), 30), new Date()]
  );
  const selectedDateRange = ref(urlRange);
  const from = ref(urlFrom || getUnixTime(startOfDay(subDays(new Date(), 30))));
  const to = ref(urlTo || getUnixTime(endOfDay(new Date())));

  // Initialize extra filters from URL
  const filters = {};
  Object.entries(filterDefs).forEach(([key, defaultValue]) => {
    filters[key] = ref(route.query[key] || defaultValue);
  });

  // GK-HUB: Agent list for dropdown filtering (only role=agent)
  const allAgents = computed(() => store.getters['agents/getAgents']);
  const filteredAgents = computed(() => {
    // GK-HUB: Filter to show only agents (exclude admin/manager/supervisor)
    return allAgents.value.filter(a => !a.role || a.role === 'agent');
  });
  // GK-HUB: end agent filtering

  const data = computed(() => store.getters[getter]);
  const error = computed(() => data.value?.error || null);

  const fetchData = () => {
    let params = { from: from.value, to: to.value };
    Object.entries(filters).forEach(([key, r]) => {
      const val = r.value;
      if (val !== '' && val !== undefined) params[key] = val;
    });
    // GK-HUB: hook opcional p/ derivar params extras a partir dos filtros (#27)
    if (paramsTransform) params = paramsTransform(params);
    // GK-HUB: Team-based scoping — supervisor auto-scoped on backend via team_ids
    // No department_id needed — backend resolves team_ids for supervisor automatically
    store.dispatch(action, params);
  };

  // URL persistence
  const updateURL = () => {
    const params = {
      from: from.value,
      to: to.value,
      range: selectedDateRange.value,
    };
    Object.entries(filters).forEach(([key, r]) => {
      if (r.value !== '' && r.value !== undefined) params[key] = r.value;
    });
    router.replace({ query: params });
  };

  // WootDatePicker handler
  const onDateRangeChange = ([startDate, endDate, rangeType]) => {
    customDateRange.value = [startDate, endDate];
    selectedDateRange.value = rangeType || 'custom';
    from.value = getUnixTime(startOfDay(startDate));
    to.value = getUnixTime(endOfDay(endDate));
    updateURL();
    fetchData();
  };

  const onFilterChange = (key, value) => {
    filters[key].value =
      typeof value === 'object' && value.target ? value.target.value : value;
    updateURL();
    fetchData();
  };

  onMounted(() => {
    initActions.forEach(a => store.dispatch(a));
    fetchData();
  });

  const retry = fetchData;

  return {
    from,
    to,
    customDateRange,
    selectedDateRange,
    filters,
    data,
    error,
    fetchData,
    retry,
    onDateRangeChange,
    onFilterChange,
    // GK-HUB: Agent list for dropdown (department filter removed — team-based scoping on backend)
    filteredAgents,
  };
}
